import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { fmtMoney, fmtDate } from "@/lib/format";
import {
  X,
  FileCsv,
  Printer,
  GridFour,
  ArrowDown,
  ArrowUp,
} from "@phosphor-icons/react";

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}
function firstDayOfMonthISO() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
}

const METHOD_ORDER = ["cash", "upi", "card", "online", "bank", "cheque"];

function downloadCsvFromString(csv, filename) {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const dl = document.createElement("a");
  dl.href = URL.createObjectURL(blob);
  dl.download = filename;
  document.body.appendChild(dl);
  dl.click();
  dl.remove();
}

function csvEscape(v) {
  const s = String(v ?? "");
  if (/[",\n]/.test(s)) return `"${s.replaceAll('"', '""')}"`;
  return s;
}

function buildMatrixCsv(payload, usedMethods) {
  const rows = [];
  rows.push([
    `Multi-Branch Cashbook · ${payload.period.from} → ${payload.period.to}`,
  ]);
  rows.push([]);
  const header = ["Branch", "Code"];
  usedMethods.forEach((m) => header.push(`${m.toUpperCase()} In`));
  header.push("Receipts Total");
  usedMethods.forEach((m) => header.push(`${m.toUpperCase()} Out`));
  header.push("Payments Total", "Net");
  rows.push(header);
  payload.branches.forEach((b) => {
    const r = [
      b.branch.name || b.branch.id,
      b.branch.code || "",
    ];
    usedMethods.forEach((m) => r.push(b.totals.receipts_by_method?.[m] || 0));
    r.push(b.totals.receipts);
    usedMethods.forEach((m) => r.push(b.totals.payments_by_method?.[m] || 0));
    r.push(b.totals.payments, b.totals.net);
    rows.push(r);
  });
  // Grand totals row
  const g = payload.grand_totals;
  const gr = ["GRAND TOTAL", ""];
  usedMethods.forEach((m) => gr.push(g.receipts_by_method?.[m] || 0));
  gr.push(g.receipts);
  usedMethods.forEach((m) => gr.push(g.payments_by_method?.[m] || 0));
  gr.push(g.payments, g.net);
  rows.push(gr);
  return rows.map((r) => r.map(csvEscape).join(",")).join("\n");
}

function printMatrix(payload, usedMethods) {
  const w = window.open("", "_blank", "width=1200,height=900");
  if (!w) return;
  const esc = (s) =>
    String(s ?? "")
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;");

  const inHeaderCells = usedMethods
    .map(
      (m) =>
        `<th style="text-align:right">${esc(m.toUpperCase())} In</th>`,
    )
    .join("");
  const outHeaderCells = usedMethods
    .map(
      (m) =>
        `<th style="text-align:right">${esc(m.toUpperCase())} Out</th>`,
    )
    .join("");

  const branchRows = payload.branches
    .map((b) => {
      const inCells = usedMethods
        .map(
          (m) =>
            `<td style="text-align:right;font-family:'IBM Plex Mono',monospace">${esc(fmtMoney(b.totals.receipts_by_method?.[m] || 0))}</td>`,
        )
        .join("");
      const outCells = usedMethods
        .map(
          (m) =>
            `<td style="text-align:right;font-family:'IBM Plex Mono',monospace">${esc(fmtMoney(b.totals.payments_by_method?.[m] || 0))}</td>`,
        )
        .join("");
      const netColor = b.totals.net > 0 ? "#00713a" : b.totals.net < 0 ? "#a30000" : "#0A0A0A";
      return `<tr>
        <td><b>${esc(b.branch.name || b.branch.id)}</b><div style="color:#5C5C5C;font-family:'IBM Plex Mono',monospace;font-size:10px;letter-spacing:.1em;text-transform:uppercase">${esc(b.branch.code || "")}</div></td>
        ${inCells}
        <td style="text-align:right;font-weight:700;color:#00713a;font-family:'IBM Plex Mono',monospace">${esc(fmtMoney(b.totals.receipts))}</td>
        ${outCells}
        <td style="text-align:right;font-weight:700;color:#a30000;font-family:'IBM Plex Mono',monospace">${esc(fmtMoney(b.totals.payments))}</td>
        <td style="text-align:right;font-weight:700;color:${netColor};font-family:'IBM Plex Mono',monospace">${esc(fmtMoney(b.totals.net))}</td>
      </tr>`;
    })
    .join("");

  const g = payload.grand_totals;
  const gInCells = usedMethods
    .map(
      (m) =>
        `<td style="text-align:right;font-family:'IBM Plex Mono',monospace">${esc(fmtMoney(g.receipts_by_method?.[m] || 0))}</td>`,
    )
    .join("");
  const gOutCells = usedMethods
    .map(
      (m) =>
        `<td style="text-align:right;font-family:'IBM Plex Mono',monospace">${esc(fmtMoney(g.payments_by_method?.[m] || 0))}</td>`,
    )
    .join("");
  const gNetColor = g.net > 0 ? "#00713a" : g.net < 0 ? "#a30000" : "#0A0A0A";

  w.document.write(`<!doctype html><html><head><title>Multi-Branch Cashbook ${esc(payload.period.from)} → ${esc(payload.period.to)}</title>
  <style>
    *{box-sizing:border-box}
    body{font-family:'Work Sans',Helvetica,sans-serif;color:#0A0A0A;margin:24px;background:#fff}
    h1{font-family:'Cabinet Grotesk',sans-serif;font-size:22px;margin:0}
    .eyebrow{font-family:'IBM Plex Mono',monospace;font-size:10px;text-transform:uppercase;letter-spacing:.3em;color:#5C5C5C;margin-top:6px}
    table{width:100%;border-collapse:collapse;margin-top:16px}
    th,td{padding:8px 10px;border-bottom:1px solid #eee;font-size:12px;text-align:left}
    th{font-family:'IBM Plex Mono',monospace;font-size:10px;letter-spacing:.15em;text-transform:uppercase;color:#5C5C5C;background:#fafafa;border-bottom:1px solid #0A0A0A}
    tfoot td{background:#fafafa;border-top:2px solid #0A0A0A;font-weight:700}
    .foot{margin-top:24px;font-size:11px;font-family:'IBM Plex Mono',monospace;color:#5C5C5C;text-transform:uppercase;letter-spacing:.15em}
    @media print{button{display:none}}
  </style></head><body>
  <h1>Multi-Branch Cashbook</h1>
  <div class="eyebrow">${esc(payload.period.from)} → ${esc(payload.period.to)} · ${payload.branches.length} branches</div>
  <table>
    <thead>
      <tr>
        <th>Branch</th>
        ${inHeaderCells}
        <th style="text-align:right">Receipts Total</th>
        ${outHeaderCells}
        <th style="text-align:right">Payments Total</th>
        <th style="text-align:right">Net</th>
      </tr>
    </thead>
    <tbody>${branchRows}</tbody>
    <tfoot>
      <tr>
        <td>GRAND TOTAL</td>
        ${gInCells}
        <td style="text-align:right;color:#00713a;font-family:'IBM Plex Mono',monospace">${esc(fmtMoney(g.receipts))}</td>
        ${gOutCells}
        <td style="text-align:right;color:#a30000;font-family:'IBM Plex Mono',monospace">${esc(fmtMoney(g.payments))}</td>
        <td style="text-align:right;color:${gNetColor};font-family:'IBM Plex Mono',monospace">${esc(fmtMoney(g.net))}</td>
      </tr>
    </tfoot>
  </table>
  <div class="foot">Generated ${esc(new Date().toLocaleString())}</div>
  <script>window.onload=function(){window.print();}</script>
  </body></html>`);
  w.document.close();
}

export default function MultiBranchCashbookDialog({ open, onClose }) {
  const [dateFrom, setDateFrom] = useState(todayISO());
  const [dateTo, setDateTo] = useState(todayISO());
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/vouchers/cashbook/multi", {
        params: { date_from: dateFrom, date_to: dateTo },
      });
      setPayload(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/vouchers/cashbook/multi", {
          params: { date_from: dateFrom, date_to: dateTo },
        });
        if (!cancelled) setPayload(data);
      } catch (err) {
        if (!cancelled) toast.error(formatApiError(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Compute which methods actually have any activity (across all branches) so
  // we don't render columns full of zeros.
  const usedMethods = useMemo(() => {
    if (!payload) return [];
    const g = payload.grand_totals;
    return METHOD_ORDER.filter(
      (m) =>
        (g.receipts_by_method?.[m] || 0) > 0 ||
        (g.payments_by_method?.[m] || 0) > 0,
    );
  }, [payload]);

  // Fallback: if nothing happened in the window, still show CASH/UPI columns
  // so the grid doesn't collapse into nothing.
  const displayMethods = usedMethods.length > 0 ? usedMethods : ["cash", "upi"];

  const handleCsv = () => {
    if (!payload) return;
    const csv = buildMatrixCsv(payload, displayMethods);
    downloadCsvFromString(
      csv,
      `cashbook_matrix_${dateFrom}_${dateTo}.csv`,
    );
    toast.success("CSV downloaded");
  };

  const handlePrint = () => {
    if (!payload) return;
    printMatrix(payload, displayMethods);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="multi-branch-cashbook-dialog"
        className="bg-canvas-card border-2 border-ink w-full max-w-[1200px] max-h-[92vh] overflow-y-auto"
      >
        <div className="px-6 py-4 border-b border-canvas-line flex items-center justify-between">
          <div>
            <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-ink-muted">
              Cashbook · Cross-Branch
            </div>
            <h2 className="font-display text-xl font-bold text-ink mt-1 flex items-center gap-2">
              <GridFour size={18} weight="bold" />
              Multi-Branch Matrix
            </h2>
          </div>
          <button
            onClick={onClose}
            data-testid="close-multi-branch-dialog"
            className="p-2 border border-canvas-line hover:border-ink"
            aria-label="Close"
          >
            <X size={14} weight="bold" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">
                From
              </label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                data-testid="multi-branch-from"
                className="px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
              />
            </div>
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">
                To
              </label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                data-testid="multi-branch-to"
                className="px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
              />
            </div>
            <button
              type="button"
              onClick={load}
              disabled={loading}
              data-testid="multi-branch-apply"
              className="px-3 py-2 bg-ink text-white text-xs font-semibold hover:bg-brand disabled:opacity-60"
            >
              {loading ? "Loading…" : "Refresh"}
            </button>
            <button
              type="button"
              onClick={handleCsv}
              disabled={!payload}
              data-testid="multi-branch-csv"
              className="px-3 py-2 border border-ink text-ink text-xs font-semibold hover:bg-ink hover:text-white disabled:opacity-60 flex items-center gap-1.5"
            >
              <FileCsv size={12} weight="bold" />
              CSV
            </button>
            <button
              type="button"
              onClick={handlePrint}
              disabled={!payload}
              data-testid="multi-branch-print"
              className="px-3 py-2 border border-ink text-ink text-xs font-semibold hover:bg-ink hover:text-white disabled:opacity-60 flex items-center gap-1.5"
            >
              <Printer size={12} weight="bold" />
              Print
            </button>
          </div>

          {payload && (
            <>
              {/* KPI strip */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="multi-branch-kpis">
                <KpiTile
                  label="Receipts (in)"
                  value={payload.grand_totals.receipts}
                  tone="pos"
                />
                <KpiTile
                  label="Payments (out)"
                  value={payload.grand_totals.payments}
                  tone="neg"
                />
                <KpiTile
                  label="Net"
                  value={payload.grand_totals.net}
                  tone={
                    payload.grand_totals.net > 0
                      ? "pos"
                      : payload.grand_totals.net < 0
                        ? "neg"
                        : "neutral"
                  }
                />
              </div>

              {/* Matrix table */}
              <div className="border border-canvas-line bg-canvas-card overflow-x-auto">
                <table
                  className="w-full text-xs"
                  data-testid="multi-branch-matrix-table"
                >
                  <thead className="bg-canvas-panel border-b border-canvas-line">
                    <tr>
                      <th
                        rowSpan={2}
                        className="sticky left-0 z-10 bg-canvas-panel text-left px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted border-r border-canvas-line"
                      >
                        Branch
                      </th>
                      <th
                        colSpan={displayMethods.length + 1}
                        className="text-center px-3 py-2 font-mono text-[10px] uppercase tracking-wider text-[#00B341] border-r border-canvas-line border-b border-canvas-line"
                      >
                        <span className="inline-flex items-center gap-1">
                          <ArrowDown size={10} weight="bold" />
                          Receipts (in)
                        </span>
                      </th>
                      <th
                        colSpan={displayMethods.length + 1}
                        className="text-center px-3 py-2 font-mono text-[10px] uppercase tracking-wider text-destructive border-r border-canvas-line border-b border-canvas-line"
                      >
                        <span className="inline-flex items-center gap-1">
                          <ArrowUp size={10} weight="bold" />
                          Payments (out)
                        </span>
                      </th>
                      <th
                        rowSpan={2}
                        className="text-right px-3 py-2 font-mono text-[10px] uppercase tracking-wider text-ink-muted"
                      >
                        Net
                      </th>
                    </tr>
                    <tr>
                      {displayMethods.map((m) => (
                        <th
                          key={`in-${m}`}
                          className="text-right px-2 py-1.5 font-mono text-[9px] uppercase tracking-wider text-[#00B341]/80"
                        >
                          {m}
                        </th>
                      ))}
                      <th className="text-right px-2 py-1.5 font-mono text-[9px] uppercase tracking-wider text-[#00B341] border-r border-canvas-line">
                        Total
                      </th>
                      {displayMethods.map((m) => (
                        <th
                          key={`out-${m}`}
                          className="text-right px-2 py-1.5 font-mono text-[9px] uppercase tracking-wider text-destructive/80"
                        >
                          {m}
                        </th>
                      ))}
                      <th className="text-right px-2 py-1.5 font-mono text-[9px] uppercase tracking-wider text-destructive border-r border-canvas-line">
                        Total
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {payload.branches.length === 0 && (
                      <tr>
                        <td
                          colSpan={5 + displayMethods.length * 2 + 2}
                          className="px-3 py-10 text-center font-mono text-xs text-ink-subtle"
                        >
                          No branches accessible in this window.
                        </td>
                      </tr>
                    )}
                    {payload.branches.map((b) => (
                      <BranchRow
                        key={b.branch.id}
                        branch={b}
                        methods={displayMethods}
                      />
                    ))}
                  </tbody>
                  {payload.branches.length > 0 && (
                    <tfoot>
                      <GrandTotalRow
                        grand={payload.grand_totals}
                        methods={displayMethods}
                      />
                    </tfoot>
                  )}
                </table>
              </div>

              <p className="font-mono text-[10px] text-ink-subtle">
                Columns hidden when no branch had activity in that method. Use
                CSV export for the full breakdown.
              </p>
            </>
          )}

          {!payload && !loading && (
            <div className="border border-dashed border-canvas-line p-12 text-center font-mono text-xs text-ink-subtle">
              No data yet — pick a date range and hit Refresh.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function KpiTile({ label, value, tone = "neutral" }) {
  const cls =
    tone === "pos"
      ? "border-[#00B341] bg-[#00B341]/5 text-[#00B341]"
      : tone === "neg"
        ? "border-destructive bg-destructive/5 text-destructive"
        : "border-canvas-line bg-canvas-card text-ink";
  return (
    <div
      className={`border p-3 ${cls}`}
      data-testid={`multi-branch-kpi-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}
    >
      <div className="font-mono text-[9px] uppercase tracking-[0.2em] opacity-80">
        {label}
      </div>
      <div className="font-display text-lg font-bold mt-1 tabular-nums">
        {fmtMoney(value)}
      </div>
    </div>
  );
}

function BranchRow({ branch, methods }) {
  const t = branch.totals;
  const netColor =
    t.net > 0
      ? "text-[#00B341]"
      : t.net < 0
        ? "text-destructive"
        : "text-ink";
  return (
    <tr
      data-testid={`multi-branch-row-${branch.branch.id}`}
      className="border-b border-canvas-line last:border-b-0 hover:bg-canvas-panel/40"
    >
      <td className="sticky left-0 z-10 bg-canvas-card px-3 py-2 border-r border-canvas-line">
        <div className="font-semibold text-ink">{branch.branch.name}</div>
        <div className="font-mono text-[9px] uppercase tracking-wider text-ink-subtle">
          {branch.branch.code}
        </div>
      </td>
      {methods.map((m) => {
        const v = t.receipts_by_method?.[m] || 0;
        return (
          <td
            key={`in-${m}`}
            className={`px-2 py-2 text-right font-mono tabular-nums text-[11px] ${v > 0 ? "text-[#00B341]" : "text-ink-subtle"}`}
            data-testid={`mb-${branch.branch.id}-in-${m}`}
          >
            {v > 0 ? fmtMoney(v) : "—"}
          </td>
        );
      })}
      <td
        className="px-2 py-2 text-right font-mono tabular-nums font-semibold text-[#00B341] border-r border-canvas-line"
        data-testid={`mb-${branch.branch.id}-in-total`}
      >
        {fmtMoney(t.receipts)}
      </td>
      {methods.map((m) => {
        const v = t.payments_by_method?.[m] || 0;
        return (
          <td
            key={`out-${m}`}
            className={`px-2 py-2 text-right font-mono tabular-nums text-[11px] ${v > 0 ? "text-destructive" : "text-ink-subtle"}`}
            data-testid={`mb-${branch.branch.id}-out-${m}`}
          >
            {v > 0 ? fmtMoney(v) : "—"}
          </td>
        );
      })}
      <td
        className="px-2 py-2 text-right font-mono tabular-nums font-semibold text-destructive border-r border-canvas-line"
        data-testid={`mb-${branch.branch.id}-out-total`}
      >
        {fmtMoney(t.payments)}
      </td>
      <td
        className={`px-3 py-2 text-right font-mono tabular-nums font-semibold border-r border-canvas-line ${netColor}`}
        data-testid={`mb-${branch.branch.id}-net`}
      >
        {fmtMoney(t.net)}
      </td>
      <td
        className="px-3 py-2 text-right font-mono tabular-nums font-bold text-ink"
        data-testid={`mb-${branch.branch.id}-closing`}
      >
        {fmtMoney(branch.closing_balance)}
      </td>
    </tr>
  );
}

function GrandTotalRow({ grand, methods }) {
  const netColor =
    grand.net > 0
      ? "text-[#00B341]"
      : grand.net < 0
        ? "text-destructive"
        : "text-ink";
  return (
    <tr
      data-testid="multi-branch-grand-total"
      className="bg-canvas-panel border-t-2 border-ink"
    >
      <td className="sticky left-0 z-10 bg-canvas-panel px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] font-bold border-r border-canvas-line">
        Grand Total
      </td>
      {methods.map((m) => (
        <td
          key={`g-in-${m}`}
          className="px-2 py-2 text-right font-mono tabular-nums font-bold text-[11px] text-[#00B341]"
        >
          {grand.receipts_by_method?.[m]
            ? fmtMoney(grand.receipts_by_method[m])
            : "—"}
        </td>
      ))}
      <td className="px-2 py-2 text-right font-mono tabular-nums font-bold text-[#00B341] border-r border-canvas-line">
        {fmtMoney(grand.receipts)}
      </td>
      {methods.map((m) => (
        <td
          key={`g-out-${m}`}
          className="px-2 py-2 text-right font-mono tabular-nums font-bold text-[11px] text-destructive"
        >
          {grand.payments_by_method?.[m]
            ? fmtMoney(grand.payments_by_method[m])
            : "—"}
        </td>
      ))}
      <td className="px-2 py-2 text-right font-mono tabular-nums font-bold text-destructive border-r border-canvas-line">
        {fmtMoney(grand.payments)}
      </td>
      <td
        className={`px-3 py-2 text-right font-mono tabular-nums font-bold ${netColor}`}
      >
        {fmtMoney(grand.net)}
      </td>
    </tr>
  );
}
